<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import ContactBilling from 'dashboard/api/contactBilling';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { contactBillingCopy as copy } from './contactBillingCopy';

const props = defineProps({
  contact: { type: Object, required: true },
  conversationId: { type: [Number, String], default: null },
});
const dialog = ref(null);
const confirmation = ref(null);
const removal = ref(null);
const links = ref([]);
const integrations = ref([]);
const canManage = ref(false);
const busy = ref(false);
const selectedId = ref('');
const editing = ref(false);
const charges = ref([]);
const page = ref(1);
const hasMore = ref(false);
const loaded = ref(false);
const preview = ref(null);
const form = reactive({
  id: null,
  integration_hook_id: '',
  external_customer_id: '',
  external_subscription_id: '',
});
let generation = 0;

const selected = computed(() =>
  links.value.find(link => String(link.id) === String(selectedId.value))
);
const integration = computed(() =>
  integrations.value.find(
    item => item.id === selected.value?.integration_hook_id
  )
);
const newIntegrations = computed(() =>
  integrations.value.filter(
    item =>
      item.available &&
      !links.value.some(link => link.integration_hook_id === item.id)
  )
);
const integrationOptions = computed(() =>
  form.id ? integrations.value : newIntegrations.value
);
const ready = computed(
  () =>
    !busy.value &&
    !editing.value &&
    selected.value &&
    integration.value?.available
);
const statuses = {
  PENDING: 'Em aberto',
  OVERDUE: 'Vencida',
  RECEIVED: 'Paga',
  CONFIRMED: 'Confirmada',
  RECEIVED_IN_CASH: 'Paga em dinheiro',
  REFUNDED: 'Estornada',
  REFUND_REQUESTED: 'Estorno solicitado',
  CHARGEBACK_REQUESTED: 'Contestada',
  CHARGEBACK_DISPUTE: 'Em contestação',
  AWAITING_CHARGEBACK_REVERSAL: 'Em contestação',
  AWAITING_RISK_ANALYSIS: 'Em análise',
  DUNNING_REQUESTED: 'Negativação solicitada',
  DUNNING_RECEIVED: 'Recebida após negativação',
};
const money = charge =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: charge.currency,
  }).format(charge.amount_cents / 100);
const date = value => value?.split('-').reverse().join('/') || 'Sem vencimento';

const clearCharges = () => {
  charges.value = [];
  loaded.value = false;
  page.value = 1;
  hasMore.value = false;
  preview.value = null;
};
watch(selectedId, clearCharges);

const cancelPending = () => {
  generation += 1;
  busy.value = false;
  preview.value = null;
  confirmation.value?.close();
  removal.value?.close();
};
watch(
  () => props.contact.id,
  () => {
    cancelPending();
    dialog.value?.close();
  }
);
onBeforeUnmount(cancelPending);

const run = async (request, apply) => {
  if (busy.value) return;
  const version = generation;
  busy.value = true;
  try {
    const response = await request();
    if (version === generation) apply(response.data);
  } catch (error) {
    if (version === generation)
      useAlert(
        error.response?.data?.error ||
          'Não foi possível concluir a operação. Tente novamente.'
      );
  } finally {
    if (version === generation) busy.value = false;
  }
};

const startEditing = link => {
  clearCharges();
  Object.assign(form, {
    id: link?.id || null,
    integration_hook_id:
      link?.integration_hook_id || newIntegrations.value[0]?.id || '',
    external_customer_id: link?.external_customer_id || '',
    external_subscription_id: link?.external_subscription_id || '',
  });
  editing.value = true;
};

const open = () => {
  if (busy.value) return;
  generation += 1;
  clearCharges();
  run(
    () => ContactBilling.getLinks(props.contact.id),
    data => {
      links.value = data.links;
      integrations.value = data.integrations;
      canManage.value = data.can_manage;
      selectedId.value = data.links[0]?.id || '';
      editing.value = false;
      if (
        !data.links.length &&
        data.can_manage &&
        data.integrations.some(item => item.available)
      )
        startEditing();
      dialog.value.open();
    }
  );
};

const save = () =>
  run(
    () =>
      ContactBilling.saveLink(props.contact.id, form.id, {
        integration_hook_id: Number(form.integration_hook_id),
        external_customer_id: form.external_customer_id.trim(),
        external_subscription_id: form.external_subscription_id.trim(),
      }),
    data => {
      links.value = [
        ...links.value.filter(link => link.id !== data.link.id),
        data.link,
      ];
      selectedId.value = data.link.id;
      editing.value = false;
      clearCharges();
      useAlert('Vínculo conferido no banco e salvo no contato.', {
        variant: 'positive',
      });
    }
  );

const consult = (nextPage = 1) =>
  run(
    () => ContactBilling.charges(props.contact.id, selected.value.id, nextPage),
    data => {
      charges.value = data.charges;
      page.value = data.page;
      hasMore.value = data.has_more;
      loaded.value = true;
    }
  );

const prepare = charge =>
  run(
    () =>
      ContactBilling.preview(props.contact.id, selected.value.id, {
        charge_id: charge.id,
        conversation_id: props.conversationId,
      }),
    data => {
      preview.value = { ...data, linkId: selected.value.id };
      confirmation.value.open();
    }
  );

const send = () => {
  if (!preview.value) return;
  run(
    () =>
      ContactBilling.sendCharge(props.contact.id, preview.value.linkId, {
        token: preview.value.token,
        conversation_id: props.conversationId,
      }),
    () => {
      confirmation.value.close();
      preview.value = null;
      useAlert(
        'Cobrança enfileirada para envio nesta conversa. Acompanhe o status da mensagem.',
        { variant: 'positive' }
      );
    }
  );
};

const remove = () =>
  run(
    () => ContactBilling.removeLink(props.contact.id, selected.value.id),
    () => {
      links.value = links.value.filter(link => link.id !== selected.value.id);
      selectedId.value = links.value[0]?.id || '';
      clearCharges();
      removal.value.close();
      useAlert('Vínculo removido do contato.');
    }
  );
</script>

<template>
  <slot :open="open" :is-loading="busy">
    <Button
      type="button"
      label="Cobranças do contato"
      icon="i-lucide-landmark"
      size="sm"
      :is-loading="busy"
      @click="open"
    />
  </slot>
  <Dialog
    ref="dialog"
    :title="copy.title"
    :description="`Consulte boletos e cobranças de ${contact.name || 'este contato'} na integração bancária da empresa.`"
    :show-confirm-button="false"
    cancel-button-label="Fechar"
    :close-on-click-outside="!busy"
    solid
    overflow-y-auto
    sticky-header-footer
    width="2xl"
    @close="cancelPending"
  >
    <p
      v-if="!integrations.some(item => item.available)"
      class="text-sm text-n-slate-11"
    >
      {{ copy.unavailable }}
    </p>
    <div v-if="links.length" class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12" for="billing-link">{{
        copy.link
      }}</label>
      <select
        id="billing-link"
        v-model="selectedId"
        :disabled="busy || editing"
        class="mb-0 w-full rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
      >
        <option v-for="link in links" :key="link.id" :value="link.id">
          {{
            integrations.find(item => item.id === link.integration_hook_id)
              ?.name || link.provider
          }}
          · {{ link.external_customer_id }}
        </option>
      </select>
      <p v-if="selected" class="break-words text-sm text-n-slate-11">
        {{ copy.customer }}
        <strong>{{
          selected.customer_name || selected.external_customer_id
        }}</strong>
        <span v-if="selected.external_subscription_id" class="block">
          {{ copy.subscription }} {{ selected.external_subscription_id }}
        </span>
      </p>
    </div>
    <div v-if="canManage && !editing" class="flex flex-wrap gap-2">
      <Button
        type="button"
        label="Adicionar vínculo"
        size="sm"
        variant="outline"
        :disabled="busy || !newIntegrations.length"
        @click="startEditing()"
      />
      <Button
        type="button"
        v-if="selected"
        label="Editar códigos"
        size="sm"
        variant="outline"
        :disabled="busy"
        @click="startEditing(selected)"
      />
      <Button
        type="button"
        v-if="selected"
        label="Remover vínculo"
        size="sm"
        variant="outline"
        color="ruby"
        :disabled="busy"
        @click="removal.open()"
      />
    </div>
    <div
      v-if="editing"
      class="flex flex-col gap-4 rounded-lg border border-n-weak p-4"
    >
      <label
        class="text-sm font-medium text-n-slate-12"
        for="billing-integration"
        >{{ copy.integration }}</label
      >
      <select
        id="billing-integration"
        v-model="form.integration_hook_id"
        :disabled="busy || !!form.id"
        class="mb-0 w-full rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
      >
        <option value="" disabled>{{ copy.selectIntegration }}</option>
        <option
          v-for="item in integrationOptions"
          :key="item.id"
          :value="item.id"
          :disabled="!item.available"
        >
          {{ item.name }}
        </option>
      </select>
      <Input
        v-model="form.external_customer_id"
        label="ID do cliente no banco"
        placeholder="Ex.: cus_000000000000"
        :disabled="busy"
      />
      <Input
        v-model="form.external_subscription_id"
        label="Código da assinatura (opcional)"
        placeholder="Ex.: sub_000000000000"
        :disabled="busy"
      />
      <p class="text-xs text-n-slate-11">
        {{ copy.identifiersHelp }}
      </p>
      <div class="flex flex-wrap gap-2">
        <Button
          type="button"
          label="Conferir e salvar vínculo"
          size="sm"
          :is-loading="busy"
          :disabled="
            !form.integration_hook_id ||
            (!form.external_customer_id.trim() &&
              !form.external_subscription_id.trim())
          "
          @click="save"
        />
        <Button
          type="button"
          label="Cancelar edição"
          size="sm"
          variant="outline"
          :disabled="busy"
          @click="editing = false"
        />
      </div>
    </div>
    <div v-if="selected && !editing" class="flex flex-col gap-3">
      <Button
        type="button"
        label="Consultar cobranças"
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="busy"
        :disabled="!ready"
        class="self-start"
        @click="consult(1)"
      />
      <p v-if="!conversationId" class="text-xs text-n-slate-11">
        {{ copy.openConversation }}
      </p>
      <p v-else class="text-xs text-n-slate-11">
        {{ copy.windowHelp }}
      </p>
      <p
        v-if="integration && !integration.can_send"
        class="text-xs text-n-slate-11"
      >
        {{ copy.sendDisabled }}
      </p>
      <p v-if="loaded && !charges.length" class="text-sm text-n-slate-11">
        {{ copy.empty }}
      </p>
      <article
        v-for="charge in charges"
        :key="charge.id"
        class="flex flex-col gap-2 rounded-lg border border-n-weak p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-2 text-sm">
          <strong class="text-n-slate-12">{{ money(charge) }}</strong>
          <span class="text-n-slate-11">{{
            statuses[charge.status] || 'Indisponível para envio'
          }}</span>
        </div>
        <p class="mb-0 break-words text-sm text-n-slate-12">
          {{ charge.description || 'Cobrança' }}
        </p>
        <p class="mb-0 break-words text-xs text-n-slate-11">
          {{ copy.due }} {{ date(charge.due_date) }} · {{ charge.id }}
        </p>
        <div class="flex flex-wrap items-center gap-3">
          <a
            v-if="charge.payment_url"
            :href="charge.payment_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-n-brand"
            >{{ copy.openPayment }}</a
          >
          <Button
            type="button"
            v-if="conversationId"
            label="Revisar envio no WhatsApp"
            size="sm"
            :disabled="busy || !charge.sendable || !integration?.can_send"
            @click="prepare(charge)"
          />
        </div>
      </article>
      <div v-if="loaded" class="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          label="Anterior"
          size="sm"
          variant="outline"
          :disabled="busy || page === 1"
          @click="consult(page - 1)"
        />
        <span class="text-sm text-n-slate-11">{{ copy.page }} {{ page }}</span>
        <Button
          type="button"
          label="Próxima"
          size="sm"
          variant="outline"
          :disabled="busy || !hasMore"
          @click="consult(page + 1)"
        />
      </div>
    </div>
  </Dialog>
  <Dialog
    ref="confirmation"
    :title="copy.confirmationTitle"
    :description="`${preview?.contact_name || ''} · ${preview?.phone_number || ''}`"
    confirm-button-label="Confirmar envio"
    cancel-button-label="Voltar"
    :disable-confirm-button="busy || !preview"
    :is-loading="busy"
    :close-on-click-outside="!busy"
    solid
    overflow-y-auto
    sticky-header-footer
    @confirm="send"
  >
    <p
      class="whitespace-pre-wrap break-words rounded-lg border border-n-weak p-4 text-sm text-n-slate-12"
    >
      {{ preview?.content }}
    </p>
  </Dialog>
  <Dialog
    ref="removal"
    :title="copy.removalTitle"
    description="Remove somente os códigos salvos neste contato. O cadastro, a assinatura e as cobranças continuam no banco."
    confirm-button-label="Remover vínculo"
    cancel-button-label="Cancelar"
    :is-loading="busy"
    :disable-confirm-button="busy"
    :close-on-click-outside="!busy"
    solid
    @confirm="remove"
  />
</template>
